// Generated by GitHub Copilot
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Spinner, Alert, Row, Col } from "react-bootstrap";
import { FaShare, FaMeteor } from "react-icons/fa";
import ImpactSummaryDashboard from "./ImpactSummaryDashboard";
import BrandGrid from "./BrandGrid";
import ImpactRecordsList from "./ImpactRecordsList";
import ImpactFormModal from "./ImpactFormModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import ShareLinkModal from "./ShareLinkModal";
import { impactService } from "../../services/impactService";
import { canDeleteImpact } from "../../utils/privilegeHelpers";

import styles from "./ImpactView.module.css";

function ImpactView({
  problemNumber,
  problemTitle,
  priority,
  onBack,
  user,
  onReportModalChange,
}) {
  // Generated by GitHub Copilot - Use URL search params for brand modal state
  const [searchParams, setSearchParams] = useSearchParams();
  // Generated by GitHub Copilot - Add navigate hook for privilege error redirection
  const navigate = useNavigate();

  // US1971073: hard-delete of erroneous impact entries is restricted to the
  // MIM/Eternium personas (impact_delete) or deity. The server re-enforces this;
  // gating here HIDES the Delete button from everyone else (thin visibility gate).
  const canDelete = canDeleteImpact(user);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [impacts, setImpacts] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [editingImpact, setEditingImpact] = useState(null);
  const [deletingImpact, setDeletingImpact] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Generated by GitHub Copilot - Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  // Generated by GitHub Copilot - Animation state for overview section
  const [showOverview, setShowOverview] = useState(true);
  // Generated by GitHub Copilot - US1812367: Track if we've processed the openModal param
  const openModalProcessed = React.useRef(false);

  // Generated by GitHub Copilot - Get brand and status from URL params for filtering
  const selectedBrand = searchParams.get("brand");
  const selectedStatus = searchParams.get("status");

  // Generated by GitHub Copilot - Filter impacts by selected brand and status
  const filteredImpacts = useMemo(() => {
    let filtered = impacts;

    // Apply brand filter
    if (selectedBrand) {
      filtered = filtered.filter((impact) => impact.brand === selectedBrand);
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter((impact) => impact.status === selectedStatus);
    }

    return filtered;
  }, [impacts, selectedBrand, selectedStatus]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load summary, impacts, and problem data in parallel
      const [summaryResult, impactsResult] = await Promise.all([
        impactService.getSummary(problemNumber),
        impactService.getImpacts(problemNumber),
      ]);

      setSummary(summaryResult);
      setImpacts(impactsResult.impacts);
    } catch (err) {
      console.error("Failed to load impact data:", err);
      // Generated by GitHub Copilot
      // Check if this is a privilege error (403) and redirect to home
      if (err.name === "PrivilegeError") {
        console.warn("User lacks editor privilege, redirecting to home");
        navigate("/");
        return;
      }
      setError("Failed to load impact data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemNumber]);

  // Generated by GitHub Copilot - US1812367: Auto-open modal when ?openModal=true is in URL (only on initial mount)
  useEffect(() => {
    // Only process once to prevent re-triggering
    if (openModalProcessed.current) {
      return;
    }

    const openModal = searchParams.get("openModal");
    if (openModal === "true") {
      openModalProcessed.current = true;
      // Open the modal
      setShowReportModal(true);
      if (onReportModalChange) {
        onReportModalChange(true);
      }
      // Clean up the URL using React Router's setSearchParams to properly update state
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("openModal");
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReportImpact = () => {
    setShowReportModal(true);
    // Generated by GitHub Copilot
    if (onReportModalChange) {
      onReportModalChange(true);
    }
  };

  // Generated by GitHub Copilot - Update URL params when brand is clicked
  const handleBrandClick = (brandName) => {
    // Toggle: if clicking the same brand, clear filter; otherwise set new filter
    if (selectedBrand === brandName) {
      setSearchParams({});
    } else {
      // Hide overview with animation before showing filtered view
      setShowOverview(false);
      // Wait for slide-up animation to complete before updating URL
      setTimeout(() => {
        setSearchParams({ brand: brandName });
      }, 360);
    }
  };

  // Generated by GitHub Copilot - US1812367: Handle status filter clicks with active/resolved
  const handleStatusClick = (statusTitle) => {
    // Map display titles to status values
    const statusMap = {
      Total: null, // Clear all filters
      Active: "active",
      Resolved: "resolved",
    };

    const statusValue = statusMap[statusTitle];

    // If Total clicked or same status clicked, clear status filter
    if (!statusValue || selectedStatus === statusValue) {
      // Keep existing brand filter, only clear status
      const newParams = {};
      if (selectedBrand) {
        newParams.brand = selectedBrand;
      }
      setSearchParams(newParams);
    } else {
      // Set new status filter, preserve existing brand filter
      const newParams = { status: statusValue };
      if (selectedBrand) {
        newParams.brand = selectedBrand;
      }
      setSearchParams(newParams);
    }
  };

  // Generated by GitHub Copilot - Clear all filters
  const handleClearFilter = () => {
    setSearchParams({});
    // Show overview when clearing all filters
    setTimeout(() => {
      setShowOverview(true);
    }, 100);
  };

  // Generated by GitHub Copilot - Clear individual filters
  const clearBrandFilter = () => {
    const newParams = {};
    if (selectedStatus) {
      newParams.status = selectedStatus;
    }
    setSearchParams(newParams);
  };

  const clearStatusFilter = () => {
    const newParams = {};
    if (selectedBrand) {
      newParams.brand = selectedBrand;
    }
    setSearchParams(newParams);
  };

  // Generated by GitHub Copilot - Show overview when no filters are selected
  useEffect(() => {
    if (!selectedBrand && !selectedStatus) {
      setShowOverview(true);
    }
  }, [selectedBrand, selectedStatus]);

  const handleEditImpact = (impact) => {
    setEditingImpact(impact);
    setShowReportModal(true);
    // Generated by GitHub Copilot
    if (onReportModalChange) {
      onReportModalChange(true);
    }
  };

  const handleDeleteImpact = (impact) => {
    setDeletingImpact(impact);
    setShowDeleteModal(true);
  };

  // Generated by GitHub Copilot - Show share modal
  const handleShareLink = () => {
    setShowShareModal(true);
  };

  const handleDeleteConfirm = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await impactService.deleteImpact(problemNumber, deletingImpact.id);

      // Reload data to reflect deletion
      await loadData();

      // Close modal and show success message
      setShowDeleteModal(false);
      setDeletingImpact(null);
      setSuccessMessage(
        `Impact for ${deletingImpact.applicationName} deleted successfully!`
      );

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Failed to delete impact:", err);
      // Generated by GitHub Copilot
      // Check if this is a privilege error (403) and redirect to home
      if (err.name === "PrivilegeError") {
        console.warn("User lacks editor privilege, redirecting to home");
        navigate("/");
        return;
      }
      setError(err.message || "Failed to delete impact. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitImpact = async (formData) => {
    setSubmitting(true);
    setError(null);

    try {
      // Generated by GitHub Copilot - US1812367: Updated status display map
      const statusDisplayMap = {
        active: "Red",
        resolved: "Green",
      };

      const impactData = {
        ...formData,
        statusDisplay: statusDisplayMap[formData.status],
      };

      // Normalize CI to uppercase for consistent case-insensitive comparison
      if (impactData.ci) {
        impactData.ci = impactData.ci.trim().toUpperCase();
      }

      if (editingImpact) {
        // Update existing impact
        await impactService.updateImpact(
          problemNumber,
          editingImpact.id,
          impactData
        );
        setSuccessMessage("Impact updated successfully!");
      } else {
        // Create new impact
        impactData.createdAt = new Date().toISOString();
        impactData.updatedAt = new Date().toISOString();
        await impactService.createImpact(problemNumber, impactData);
        setSuccessMessage("Impact reported successfully!");
      }

      // Reload data to show changes
      await loadData();

      // Close modal and reset state
      setShowReportModal(false);
      setEditingImpact(null);
      // Generated by GitHub Copilot
      if (onReportModalChange) {
        onReportModalChange(false);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Failed to save impact:", err);
      // Generated by GitHub Copilot
      // Check if this is a privilege error (403) and redirect to home
      if (err.name === "PrivilegeError") {
        console.warn("User lacks editor privilege, redirecting to home");
        navigate("/");
        return;
      }
      // Handle duplicate impact conflict (409) - show specific warning
      if (err.name === "DuplicateImpactError") {
        setError(
          "An impact report already exists for this CI in this problem. " +
            "Please review the existing report instead of creating a duplicate."
        );
        return;
      }
      setError(
        editingImpact
          ? "Failed to update impact. Please try again."
          : "Failed to report impact. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading impact data...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading Impact Command Center...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Success Message */}
      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMessage("")}
          data-testid="impact-success-alert"
        >
          {successMessage}
        </Alert>
      )}

      {/* Problem Title Section - matching Viewer style */}
      <div className={styles.problemHeader}>
        <div className="d-flex align-items-center gap-2 mb-2">
          <div
            className={styles.problemNumberBadge}
            onClick={onBack}
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
          >
            {problemNumber}
          </div>
          <Button
            variant="outline-info"
            size="sm"
            onClick={handleShareLink}
            title="Share this page"
          >
            <FaShare />
          </Button>
          {/* US1812367: Prominent Report Impact button - always shown (component only rendered for non-temporary problems) */}
          <Button
            variant="success"
            size="sm"
            onClick={handleReportImpact}
            title="Report Impact"
            className={styles.reportImpactBtn}
            data-testid="report-impact-button"
          >
            <FaMeteor className="me-2" />
            Report Impact
          </Button>
        </div>
        <Row className="align-items-center mb-3">
          <Col xs="auto">
            <div className={`${styles.priorityBadge} ${priority || "EP4"}`}>
              {priority || "EP4"}
            </div>
          </Col>
          <Col>
            <div className={styles.problemDescription}>
              {problemTitle || "No Description"}
            </div>
          </Col>
        </Row>
      </div>

      {/* Impact Reports Section - always shown (component only rendered for non-temporary problems) */}
      {/* No Impact Records message */}
          {impacts.length === 0 ? (
            <Alert variant="info" className="text-center mt-4">
              <h4>No Impact Records</h4>
              <p className="mb-0">
                No impacts have been reported for this incident yet. Click
                "Report Impact" to add the first impact record.
              </p>
            </Alert>
          ) : (
            <>
              {/* Summary Dashboard Section - Generated by GitHub Copilot - Animated visibility */}
              {((!selectedBrand && !selectedStatus) || showOverview) && (
                <div
                  className={`${styles.summarySection} ${
                    !selectedBrand && !selectedStatus
                      ? styles.slideDown
                      : styles.slideUp
                  }`}
                >
                  <h3 className={styles.sectionTitle}>Overview</h3>
                  <ImpactSummaryDashboard
                    impacts={impacts}
                    onStatusClick={handleStatusClick}
                    selectedStatus={
                      selectedStatus
                        ? selectedStatus.charAt(0).toUpperCase() +
                          selectedStatus.slice(1)
                        : null
                    }
                  />
                </div>
              )}

              {/* Generated by GitHub Copilot - Filter Breadcrumbs */}
              {(selectedBrand || selectedStatus) && (
                <div className={styles.filterBreadcrumbs}>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="text-muted small">Active Filters:</span>

                    {selectedBrand && (
                      <span className="badge bg-primary d-flex align-items-center gap-1">
                        Brand: {selectedBrand}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          aria-label="Clear brand filter"
                          onClick={clearBrandFilter}
                          style={{ fontSize: "0.7em" }}
                        ></button>
                      </span>
                    )}

                    {selectedStatus && (
                      <span className="badge bg-success d-flex align-items-center gap-1">
                        Status:{" "}
                        {selectedStatus.charAt(0).toUpperCase() +
                          selectedStatus.slice(1)}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          aria-label="Clear status filter"
                          onClick={clearStatusFilter}
                          style={{ fontSize: "0.7em" }}
                        ></button>
                      </span>
                    )}

                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleClearFilter}
                      aria-label="Clear all filters"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}

              {/* Brand Breakdown Section */}
              <div
                className={`${styles.brandSection} ${
                  selectedBrand || selectedStatus ? styles.slideDown : ""
                }`}
              >
                <Row className="mb-3 align-items-center">
                  <Col xs={12} sm={8} md={9}>
                    <h3 className={styles.sectionTitle}>Brand Breakdown</h3>
                  </Col>
                  {(selectedBrand || selectedStatus) && (
                    <Col
                      xs={12}
                      sm={4}
                      md={3}
                      className="text-sm-end mt-2 mt-sm-0"
                    >
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleClearFilter}
                      >
                        Clear All Filters
                      </Button>
                    </Col>
                  )}
                </Row>
                <BrandGrid
                  brandData={summary?.byBrand}
                  onBrandClick={handleBrandClick}
                  selectedBrand={selectedBrand}
                />
              </div>

              {/* Impact Records List Section - Generated by GitHub Copilot - Animated when filtered */}
              <div
                className={`${styles.recordsSection} ${
                  selectedBrand || selectedStatus ? styles.slideDown : ""
                }`}
              >
                <h3 className={styles.sectionTitle}>
                  {(() => {
                    let title = "Impact Records";
                    if (selectedBrand && selectedStatus) {
                      title = `${selectedBrand} - ${
                        selectedStatus.charAt(0).toUpperCase() +
                        selectedStatus.slice(1)
                      } ${title}`;
                    } else if (selectedBrand) {
                      title = `${selectedBrand} - ${title}`;
                    } else if (selectedStatus) {
                      title = `${
                        selectedStatus.charAt(0).toUpperCase() +
                        selectedStatus.slice(1)
                      } ${title}`;
                    } else {
                      title = `All ${title}`;
                    }
                    return title;
                  })()}
                </h3>
                <ImpactRecordsList
                  impacts={filteredImpacts}
                  onEdit={handleEditImpact}
                  onDelete={canDelete ? handleDeleteImpact : undefined}
                  user={user}
                />
              </div>
            </>
          )}

          {/* Report/Edit Impact Modal */}
          <ImpactFormModal
            show={showReportModal}
            onHide={() => {
              setShowReportModal(false);
              setEditingImpact(null);
              // Generated by GitHub Copilot
              if (onReportModalChange) {
                onReportModalChange(false);
              }
            }}
            onSubmit={handleSubmitImpact}
            initialValues={editingImpact}
            loading={submitting}
            problemNumber={problemNumber}
            title={editingImpact ? "Edit Impact" : "Report New Impact"}
          />

          {/* Delete Confirmation Modal */}
          <DeleteConfirmModal
            show={showDeleteModal}
            onHide={() => {
              setShowDeleteModal(false);
              setDeletingImpact(null);
            }}
            onConfirm={handleDeleteConfirm}
            impactName={deletingImpact?.applicationName}
            loading={submitting}
          />

      {/* Generated by GitHub Copilot - Share link modal */}
      <ShareLinkModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        url={window.location.href}
        problemNumber={problemNumber}
      />
    </div>
  );
}

export default ImpactView;
